import { ChevronLeft, ChevronRight, Close, StarOutlineOutlined, StarRounded } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CompareIcon from "../../../assets/images/icons/CompareIcon";
import DeleteIcon from "../../../assets/images/icons/DeleteIcon.tsx";
import DownloadIcon from "../../../assets/images/icons/DownloadIcon";
import { APPS } from "../../../constants/AppsConstants.ts";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { PlaygroundImage } from "../../../models/playground.ts";
import iframeStore from "../../../pages/IframeNew/iframe-store.tsx";
import { isAutomotiveApp } from "../../../utils/index.ts";
import BriaButton from "../BriaButton/BriaButton";
import BriaTooltip from "../BriaTooltip/BriaTooltip";
import styles from "./FullScreenViewer.module.scss";
import FullScreenImage, { FullScreenImageType } from "./Image/FullScreenImage";

export type FullScreenViewerProps = {
	open: boolean;
	images: FullScreenImageType[];
	currentIndex?: number;
	displayMode?: "arrows" | "scroll";
	onClose: () => void;
	onDownload?: (images: FullScreenImageType[]) => void;
	onCompare?: (images: FullScreenImageType[]) => void;
	hideDownloadIcon?: boolean;
	hideDeleteIcon?: boolean;
	hideFavoriteIcon?: boolean;
	currentSelectedImage?: PlaygroundImage;
	displayImageHideIcon?: boolean;
	hidec2paIcon?: boolean;
	hideApiReferenceButton?: boolean;
	hideFeedbackButtons?: boolean;
};

const FullScreenViewer = ({
	open,
	images,
	currentIndex = 0,
	displayMode = "arrows",
	onClose,
	onDownload,
	onCompare,
	hideDownloadIcon,
	hideDeleteIcon = true,
	hideFavoriteIcon = true,
	currentSelectedImage,
	displayImageHideIcon = true,
	hidec2paIcon = true,
	hideApiReferenceButton = true,
	hideFeedbackButtons = true,
}: FullScreenViewerProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "briaImage.fullScreenView" });
	const { playgroundStore } = useAppStore();
	const { projectsStore, sessionsStore } = playgroundStore;
	const { session_id } = useParams();
	const [isOpen, setIsOpen] = useState(open);
	const [viewerLoaded, setViewerLoaded] = useState(false);
	const [currentImage, setCurrentImage] = useState(currentIndex);
	const [displayedImages, setDisplayedImages] = useState(images);
	const isCompareMode = !displayedImages.some((image) => !image.hide && !image.openCompare);
	const [zoomedImageIndex, setZoomImageIndex] = useState<number>(-1);

	useEffect(() => {
		setDisplayedImages(images);
	}, [images]);

	useEffect(() => {
		setIsOpen(open);
	}, [open]);

	useEffect(() => {
		setCurrentImage(currentIndex);
	}, [currentIndex]);

	useEffect(() => {
		setViewerLoaded(true);
	}, []);

	const handleDownload = () => {
		if (onDownload) {
			onDownload(
				displayedImages.length > 1 && displayMode === "scroll"
					? displayedImages
					: [displayedImages[currentImage]],
			);
		}
	};

	const openAllCompare = () => {
		setDisplayedImages(displayedImages.map((image) => ({ ...image, openCompare: !isCompareMode })));
		onCompare && !isCompareMode && onCompare(displayedImages);
	};

	const onPrevImage = useCallback(
		(e?: any) => {
			e?.stopPropagation();
			const prevIndex = (currentImage - 1 + displayedImages.length) % displayedImages.length;
			setCurrentImage(prevIndex);
		},
		[currentImage, displayedImages.length],
	);

	const onNextImage = useCallback(
		(e?: any) => {
			e?.stopPropagation();
			const nextIndex = (currentImage + 1) % displayedImages.length;
			setCurrentImage(nextIndex);
		},
		[currentImage, displayedImages.length],
	);

	const handleKeyPress = useCallback(
		(e?: any) => {
			if (e.key === "Escape") {
				onClose();
			} else if (displayMode === "arrows") {
				if (e.key === "ArrowLeft") {
					onPrevImage();
				} else if (e.key === "ArrowRight") {
					onNextImage();
				}
			}
		},
		[onClose, onPrevImage, onNextImage],
	);

	useEffect(() => {
		document.addEventListener("keyup", handleKeyPress);
		return () => {
			document.removeEventListener("keyup", handleKeyPress);
		};
	}, [handleKeyPress]);

	const handleClose = () => {
		setIsOpen(false);
		onClose();
	};

	const handleAddToFavorite = async () => {
		if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
			await projectsStore.handleAddImageToFavorite(
				currentSelectedImage as PlaygroundImage,
				!currentSelectedImage?.is_favorite,
			);
			updateSessionImages();
		}
	};

	const handleDelete = async () => {
		if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
			await projectsStore.handleDeleteImage((currentSelectedImage as PlaygroundImage).id);
			updateSessionImages();
		}
	};

	const updateSessionImages = () => {
		session_id && sessionsStore.getSession(session_id);
	};

	return (
		<>
			{isOpen && (
				<Box
					className={clsx(styles.overlay, viewerLoaded && styles.fadeIn)}
					onClick={() => !isCompareMode && handleClose()}
				>
					<Box className={styles.fullScreenViewerActions} onClick={(e) => e.stopPropagation()}>
						{isAutomotiveApp() && (
							<BriaTooltip title={t("delete")}>
								<IconButton
									onClick={handleDelete}
									className={clsx(styles.actionButton, styles.deleteBtn, {
										[styles.active]: isCompareMode,
									})}
								>
									<DeleteIcon />
								</IconButton>
							</BriaTooltip>
						)}
						{!hideFavoriteIcon && isAutomotiveApp() && (
							<BriaTooltip title={t("favorite")}>
								<IconButton
									onClick={handleAddToFavorite}
									className={clsx(styles.actionButton, {
										[styles.active]: isCompareMode,
									})}
								>
									{displayedImages[currentImage]?.is_favorite ? (
										<StarRounded />
									) : (
										<StarOutlineOutlined />
									)}
								</IconButton>
							</BriaTooltip>
						)}
						{displayedImages[currentImage]?.compareTo && (
							<BriaTooltip title={t("compare")}>
								<IconButton
									className={clsx(styles.actionButton, {
										[styles.active]: isCompareMode,
									})}
									onClick={openAllCompare}
								>
									<CompareIcon />
								</IconButton>
							</BriaTooltip>
						)}
						{!hideDownloadIcon &&
							(!iframeStore.isIframe() || iframeStore.iframe.config.general_config?.enable_download) && (
								<BriaTooltip title={t("download")}>
									<IconButton className={styles.actionButton} onClick={handleDownload}>
										<DownloadIcon className={styles.downloadIcon} />
									</IconButton>
								</BriaTooltip>
							)}
						<BriaButton
							className={clsx(styles.actionButton, styles.closeButton)}
							startIcon={<Close />}
							buttonType="tertiaryMedium"
							onClick={handleClose}
						>
							{t("close")}
						</BriaButton>
					</Box>
					{displayedImages.length > 1 && displayMode === "arrows" ? (
						<>
							{/* In case we want to disable this feature on compare mode, we should disable the keyboard navigation too (`handleKeyPress()`) */}
							<>
								<ChevronLeft
									className={`${styles.arrowIcon} ${styles.leftArrowIcon}`}
									onClick={onPrevImage}
								/>
								<ChevronRight
									className={`${styles.arrowIcon} ${styles.rightArrowIcon}`}
									onClick={onNextImage}
								/>
							</>
							<Box className={styles.imageCounter}>
								<Typography>
									{currentImage + 1}/{displayedImages.length}
								</Typography>
							</Box>
							<Box
								className={clsx({
									[styles.scrollable]: isCompareMode && displayedImages[currentImage].compareTo,
								})}
							>
								<FullScreenImage
									image={displayedImages[currentImage]}
									index={currentImage}
									{...{ displayMode, displayedImages, setDisplayedImages }}
									onDownload={() => {
										onDownload && onDownload([displayedImages[currentImage]]);
									}}
									onCompare={openAllCompare}
									hideDownloadIcon={hideDownloadIcon}
									hideDeleteIcon={hideDeleteIcon}
									hideFavoriteIcon={hideFavoriteIcon}
									displayHideIcon={displayImageHideIcon}
									hidec2paIcon={hidec2paIcon}
									hideApiReferenceButton={hideApiReferenceButton}
									hideFeedbackButtons={hideFeedbackButtons}
								/>
							</Box>
						</>
					) : (
						<Box className={styles.scrollable}>
							{displayedImages.map((image, index) => (
								<FullScreenImage
									key={index}
									{...{ image, index, displayMode, displayedImages, setDisplayedImages }}
									onZoom={(zoomed) => setZoomImageIndex(zoomed ? index : -1)}
									hide={zoomedImageIndex !== -1 && zoomedImageIndex !== index}
									onDownload={() => {
										onDownload && onDownload([image]);
									}}
									hideDownloadIcon={hideDownloadIcon}
									hideDeleteIcon={hideDeleteIcon}
									hideFavoriteIcon={hideFavoriteIcon}
									displayHideIcon={displayImageHideIcon}
									hidec2paIcon={hidec2paIcon}
									hideApiReferenceButton={hideApiReferenceButton}
									hideFeedbackButtons={hideFeedbackButtons}
								/>
							))}
						</Box>
					)}
				</Box>
			)}
		</>
	);
};

export default FullScreenViewer;
